import type SignInFieldModel from 'garaje/models/sign-in-field';
import type SignInFieldActionModel from 'garaje/models/sign-in-field-action';
import type Handsontable from 'handsontable';

/**
 * Defines a conditional dependency where a child field becomes required
 * based on parent field values.
 *
 * @example
 * { parentProp: "Department", triggering: new Set(["Engineering", "Sales"]) }
 * means field is required when Department equals "Engineering" or "Sales"
 */
export type ConditionalDependency = {
  parentProp: string;
  triggering: Set<string>; // Use Set for O(1) lookups instead of O(n) array.includes()
};

/**
 * Creates a Handsontable validator function that makes fields conditionally required.
 *
 * The validator checks if any parent field contains a triggering value, and if so,
 * requires the current child field to be required. Otherwise, the field remains optional.
 *
 * @param dependencies - Array of conditional dependencies to check
 * @returns Handsontable validator function
 */
export const createConditionalPresenceValidator = function (
  dependencies: Array<ConditionalDependency>,
): (this: unknown, value: string, cb: (valid: boolean) => void) => void | RegExp {
  return function (this: unknown, value: string, cb: (valid: boolean) => void): void | RegExp {
    // Handsontable binds 'this' to cellProperties at runtime, containing instance and row
    const context = this as { instance: Handsontable; row: number };
    const { instance, row } = context;
    const columns = (instance.getSettings().columns || []) as Array<{ data?: string }>;

    // Check if any parent field has a triggering value that makes this field required
    const isTriggered = dependencies.some(({ parentProp, triggering }) => {
      const parentColumnIndex = columns.findIndex((column) => column?.data === parentProp);

      // If parent column doesn't exist, this dependency can't be triggered
      // This prevents runtime errors and handles cases where parent field was removed/hidden
      if (parentColumnIndex === -1) return false;

      const parentValue = instance.getDataAtCell(row, parentColumnIndex) as string | null | undefined;
      // Convert to string to handle number/boolean dropdown values (e.g., option id "1")
      // Dropdown options might be stored as numbers but compared as strings
      // Example: Database ID 1 becomes "1" for string comparison in triggering array
      return parentValue != null && triggering.has(String(parentValue));
    });

    if (isTriggered) {
      // At least one parent has a triggering value -> this field is now required
      cb(Boolean(value && String(value).trim().length > 0));
    } else {
      // No triggering conditions met -> field is optional despite allowEmpty: false
      // allowEmpty: false shows red dot, but validator overrides to make field truly optional so no red dot for now.
      cb(true);
    }
  };
};

/**
 * Extracts conditional dependencies from a SignInField's actionable field actions.
 *
 * This function parses Ember Data relationships to find "show" actions that define
 * when this field should be required based on parent field values.
 *
 * @param field - The SignInFieldModel to extract dependencies from
 * @param escapeHTMLFn - Function to escape HTML in field names for safe use in Handsontable
 * @returns Array of conditional dependencies or empty array if none found or an error occurs
 */
export const extractConditionalDependencies = function (
  field: SignInFieldModel,
  escapeHTMLFn: (str: string) => string,
): ConditionalDependency[] {
  const dependencies: ConditionalDependency[] = [];

  const actions = field.actionableSignInFieldActions?.content;

  if (!actions) return dependencies;

  // Process each action to extract conditional dependencies
  for (const actionItem of actions) {
    const dependency = processActionForDependency(actionItem, escapeHTMLFn);
    if (!dependency) continue;

    const existingDependency = dependencies.find((dep) => dep.parentProp === dependency.parentProp);
    if (existingDependency) {
      existingDependency.triggering.add(dependency.triggeringValue);
    } else {
      dependencies.push({
        parentProp: dependency.parentProp,
        triggering: new Set([dependency.triggeringValue]),
      });
    }
  }

  return dependencies;
};

/**
 * Processes a single action item to extract dependency information.
 *
 * @param actionItem - The action item to process
 * @param escapeHTMLFn - Function to escape HTML in field names
 * @returns Dependency info if valid, null otherwise
 */
function processActionForDependency(
  actionItem: SignInFieldActionModel,
  escapeHTMLFn: (str: string) => string,
): { parentProp: string; triggeringValue: string } | null {
  // Only process "show" actions
  if (actionItem?.action !== 'show') return null;

  const optionValue = actionItem?.dropdownOption?.value;
  if (!optionValue) return null;

  // Extract parent field name from the related SignInField model
  const parentFieldName = actionItem?.signInField?.content?.name;
  if (!parentFieldName) return null;

  return {
    parentProp: escapeHTMLFn(parentFieldName),
    triggeringValue: optionValue,
  };
}

/**
 * Escapes HTML characters for safe use in Handsontable column data properties.
 */
export const escapeHTML = (unsafeStr: string): string => {
  return unsafeStr
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\./g, '&#46;');
};
